import { useCallback, useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
// import Chart from "chart.js";

const Chart2 = ({ value, score, defaultValue }) => {
  const [every15thValue, setEvery15thValue] = useState([]);
  const [isMobile] = useState(window.innerWidth < 600);

  const chart1Value = defaultValue ? parseFloat(defaultValue.toFixed(8)) : 0;

  const calculation = useCallback(async (chart1Value) => {
    const POST_DEFAULT = 1;
    const scoreArray = [];
    const results = [];

    for (let i = 0; i <= 60; i++) {
      scoreArray.push(i / 10);
    }

    for (let i = 0; i < scoreArray.length; i++) {
      const score = scoreArray[i];
      const scoreEXP = await calculateEXP(score);

      const result =
        ((scoreEXP * chart1Value) /
          (1 * POST_DEFAULT + (scoreEXP - 1) * chart1Value)) *
        POST_DEFAULT *
        100;

      results.push(result);
    }

    return results;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const data = await calculation(chart1Value);
      extractEvery15thValue(data);
    };

    fetchData();
  }, [calculation, chart1Value, defaultValue]);

  const calculateEXP = async (val) => {
    const e = 2.71828183;
    return e ** val;
  };

  const extractEvery15thValue = (data) => {
    const extractedValues = [data[0]];

    for (let i = 10; i < data.length; i += 10) {
      extractedValues.push(data[i]);
    }

    setEvery15thValue(extractedValues);
  };

  const state = {
    dataset: [value],
    labels: [0, 1, 2, 3, 4, 5, 6],
    options: {
      tooltips: { enabled: false }, // disable tooltip completely
      scales: {
        xAxes: [
          {
            gridLines: {
              display: true,
              drawBorder: true,
              drawOnChartArea: false,
              color: "#9B9B9B",
              tickMarkLength: 0,
              lineWidth: 2,
            },
            ticks: { fontColor: "#0a0303ff", fontSize: 14 },
          },
        ],
        yAxes: [
          {
            ticks: {
              min: 0,
              max: 100,
              stepSize: 10,
              padding: 10,
              callback: (point) => point,
              fontColor: "#0a0303ff",
            },
            gridLines: {
              display: true,
              drawBorder: true,
              lineWidth: 2,
              drawOnChartArea: false,
              color: "#9B9B9B",
              tickMarkLength: 0,
            },
          },
        ],
      },
      legend: {
        display: true,
      },
      responsive: true,
      maintainAspectRatio: false,
    },
  };

  const startValue = state.dataset[0];

  const generateDataSet = (score, value) => {
    const dataset = Array(11).fill(null);

    if (score >= 0 && score <= 6) {
      dataset[score] = value;
    }

    return dataset;
  };

  const margins = isMobile
    ? { top: 10, left: 10, right: 7, bottom: 19 } // Mobile
    : { top: 20, left: 20, right: 0, bottom: 20 }; // Desktop

  const data = {
    labels: state.labels,
    datasets: [
      {
        label: "score",
        data: generateDataSet(score, startValue),
        pointRadius: 8,
        pointHoverRadius: 13,
        pointBackgroundColor: "#f56336ff",
        pointBorderColor: "transparent",
        fill: false,
        borderColor: "transparent",
        borderWidth: 0,
      },
      {
        label: "Symptoms and Diagnostics",
        data: every15thValue,
        lineTension: 0.4,
        // borderColor: "#032d53ff", // BLUE-THEME
        borderColor: "#005F73", // GREEN-THEME
        borderWidth: 3,
        pointRadius: 0,
        pointHoverRadius: 0,
        pointBackgroundColor: "transparent",
        pointBorderColor: "transparent",
        spanGaps: false,
        dragData: false,
        fill: false,
      },
    ],
  };

  const showValuePlugin = {
    afterDatasetsDraw: function (chart) {
      const ctx = chart.ctx;

      chart.data.datasets.forEach((dataset, datasetIndex) => {
        if (dataset.label === "score") {
          const meta = chart.getDatasetMeta(datasetIndex);
          meta.data.forEach((point, index) => {
            const val = dataset.data[index];
            if (val !== null && val !== undefined) {
              ctx.save();
              ctx.fillStyle = "#f56336";
              ctx.font = "bold 12px sans-serif";
              const displayVal = Math.round(val) + "%";
              if (index === 0) {
                ctx.textAlign = "left";
                ctx.textBaseline = "top";
                ctx.fillText(displayVal, point._model.x + 12, point._model.y);
              } else if (
                (index === 1 || index === 2 || index === 3) &&
                isMobile
              ) {
                ctx.textAlign = "left";
                ctx.textBaseline = "right";
                ctx.fillText(displayVal, point._model.x + 12, point._model.y);
              } else {
                ctx.textAlign = "center";
                ctx.textBaseline = "top";
                ctx.fillText(displayVal, point._model.x, point._model.y + 12);
              }
              ctx.restore();
            }
          });
        }
      });
    },
  };

  const textBoxPlugin = {
    afterDatasetsDraw: function (chart) {
      const ctx = chart.ctx;

      const dataset = chart.data.datasets[0];
      const pointValue = dataset.data.find((v) => v !== null);
      if (!pointValue) return;

      const text = `Your estimated probability of having axial spondyloarthritis is ${Math.round(pointValue)}%. If it’s >33%, rheumatology referral is recommended. Discuss with your healthcare provider about next steps.`;

      const padding = isMobile ? 8 : 12;
      const maxWidth = isMobile ? 168 : 280;
      const fontSize = isMobile ? 11 : 14;
      const lineHeight = Math.round(fontSize * 1.35);
      ctx.font = `400 ${fontSize}px Arial`;

      //Wrap text
      const words = text.split(" ");
      let line = "";
      let lines = [];
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + " ";
        if (ctx.measureText(testLine).width > maxWidth - padding * 2 && n > 0) {
          lines.push(line.trim());
          line = words[n] + " ";
        } else {
          line = testLine;
        }
      }
      lines.push(line.trim());

      const boxHeight = lines.length * lineHeight + padding * 2;
      const boxWidth = maxWidth;

      const chartArea = chart.chartArea;

      // ✅ Two fixed candidate positions
      const candidates = [
        {
          x: chartArea.right - boxWidth - margins.right,
          y: chartArea.bottom - boxHeight - margins.bottom,
        },
        { x: chartArea.left + margins.left, y: chartArea.top + margins.top },
      ];

      // Pick one position → e.g., prefer top-left, else fallback bottom-right
      let chosen = candidates.find(
        (pos) =>
          pos.x >= chartArea.left &&
          pos.x + boxWidth <= chartArea.right &&
          pos.y >= chartArea.top &&
          pos.y + boxHeight <= chartArea.bottom
      );
      if (!chosen) chosen = candidates[0]; // fallback = bottom-right

      // Draw background
      ctx.save();
      ctx.fillStyle = "#f7f9fc";
      ctx.strokeStyle = "#225491";
      ctx.lineWidth = 1.5;
      ctx.fillRect(chosen.x, chosen.y, boxWidth, boxHeight);
      ctx.strokeRect(chosen.x, chosen.y, boxWidth, boxHeight);

      // Draw text
      ctx.fillStyle = "#1f2933";
      ctx.textBaseline = "top";

      let textY = chosen.y + padding;

      lines.forEach((line) => {
        ctx.fillText(line, chosen.x + padding, textY);
        textY += lineHeight;
      });
      ctx.restore();
    },
  };

  // Register plugin (for Chart.js v2)
  // Chart.plugins.unregister(showValuePlugin);
  // Chart.plugins.unregister(textBoxPlugin);
  // Chart.plugins.register(showValuePlugin);
  // Chart.plugins.register(textBoxPlugin);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        maxWidth: isMobile ? "320px" : "800px",
        height: isMobile ? "350px" : "450px",
        margin: "0 auto",
      }}
    >
      <Line
        data={data}
        options={state.options}
        plugins={[showValuePlugin, textBoxPlugin]}
      />
    </div>
  );
};

export default Chart2;
